'use client'

import { useRef, useState } from 'react'
import { Play, Heart, Trash2, GripVertical } from 'lucide-react'
import Artwork from './Artwork'
import DownloadButton from './DownloadButton'
import AddToMenu from './AddToMenu'
import Marquee from './Marquee'
import ArtistLink from './ArtistLink'
import type { PlayerTrack } from '@/lib/player-context'
import { usePlayer } from '@/lib/player-context'
import { useFavorites } from '@/lib/library'

type Props = {
  tracks: PlayerTrack[]
  numbered?: boolean
  emptyText?: string
  onRemove?: (videoId: string) => void   // si défini, affiche un bouton supprimer
  showFavoriteToggle?: boolean
  /** Si défini, active le glisser-déposer pour réordonner (déplace `from`
   *  pour l'insérer AVANT `to`, indices 0..length). */
  onReorder?: (from: number, to: number) => void
}

/**
 * Liste de titres (design « Baffle ») : lignes arrondies sur panneau nuit,
 * pochette carrée 48 px, titre gras, artiste gris, cœur violet et bouton
 * lecture rond violet à droite. Même comportement qu'avant (lecture, favoris,
 * ajout à une playlist, téléchargement, suppression, glisser-déposer).
 */
export default function TrackList({
  tracks, numbered = true, emptyText = 'Aucun titre.',
  onRemove, showFavoriteToggle = true, onReorder,
}: Props) {
  const { play, current, playing } = usePlayer()
  const { has, toggle } = useFavorites()

  // --- Glisser-déposer (souris + tactile via pointer events) ---
  const rowsRef = useRef<Array<HTMLDivElement | null>>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  const dragging = dragIndex !== null

  // Curseur d'insertion (0..length) selon la position Y du pointeur.
  const computeOver = (clientY: number): number => {
    const rows = rowsRef.current
    for (let i = 0; i < tracks.length; i++) {
      const el = rows[i]
      if (!el) continue
      const r = el.getBoundingClientRect()
      if (clientY < r.top + r.height / 2) return i
    }
    return tracks.length
  }
  const onGripDown = (i: number) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation()
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch {}
    setDragIndex(i); setOverIndex(i)
  }
  const onGripMove = (e: React.PointerEvent) => {
    if (dragIndex === null) return
    setOverIndex(computeOver(e.clientY))
  }
  const onGripUp = (e: React.PointerEvent) => {
    if (dragIndex !== null && overIndex !== null && onReorder) onReorder(dragIndex, overIndex)
    setDragIndex(null); setOverIndex(null)
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch {}
  }

  if (tracks.length === 0) {
    return emptyText ? <div className="text-sm text-white/55 px-2 py-6">{emptyText}</div> : null
  }

  const insertLine = <div className="mx-3 my-1 h-[3px] rounded-full bg-gradient-to-r from-a1 via-a2 to-a3 shadow-[0_0_12px_rgb(var(--a2)/.7)]" />

  return (
    <div className="space-y-2" style={{ touchAction: dragging ? 'none' : undefined }}>
      {tracks.map((t, i) => {
        const active = current?.videoId === t.videoId
        const fav = has(t.videoId)
        const isDragged = dragIndex === i
        const showLineBefore = dragging && overIndex === i && overIndex !== dragIndex && overIndex !== dragIndex + 1
        return (
          <div key={t.videoId + i} ref={el => { rowsRef.current[i] = el }}>
            {showLineBefore && insertLine}
            <div
              className={`group flex items-center gap-2 sm:gap-3 p-2 pr-2.5 rounded-2xl border transition min-w-0
                ${active ? 'bg-spanel2 border-a2/50' : 'bg-spanel/80 border-white/[0.06] hover:bg-spanel2'} ${isDragged ? 'opacity-40' : ''}`}>
              {/* Poignée de déplacement — seulement si le réordonnancement est activé */}
              {onReorder && (
                <button
                  onPointerDown={onGripDown(i)} onPointerMove={onGripMove} onPointerUp={onGripUp} onPointerCancel={onGripUp}
                  title="Glisser pour déplacer"
                  className="shrink-0 w-6 h-10 -mr-1 flex items-center justify-center text-white/30 hover:text-a4 cursor-grab active:cursor-grabbing touch-none"
                  style={{ touchAction: 'none' }}>
                  <GripVertical size={16} />
                </button>
              )}
              <button onClick={() => play(t, tracks)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left">
                {numbered && <span className={`hidden sm:inline w-7 text-center text-[16px] font-semibold tabular-nums shrink-0 ${active ? 'text-a4' : 'text-white/45'}`}>{i + 1}</span>}
                <span className="relative w-[72px] h-[72px] md:w-20 md:h-20 shrink-0 rounded-xl overflow-hidden bg-spanel3">
                  <Artwork track={t} fill strict size={96} rounded="rounded-xl" />
                  {active && playing && (
                    <span className="absolute inset-0 bg-adeep/60 flex items-end justify-center gap-0.5 pb-3">
                      {[0, 1, 2].map(k => <span key={k} className="w-[3px] h-4 bg-white rounded-sm origin-bottom animate-equalizer" style={{ animationDelay: `${k * 0.2}s` }} />)}
                    </span>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <Marquee className={`text-[17px] md:text-[19px] font-semibold ${active ? 'text-a5' : ''}`}>
                    <span dangerouslySetInnerHTML={{ __html: decodeEntities(t.title) }} />
                  </Marquee>
                  <ArtistLink artist={t.artist} className="block text-[14px] md:text-[15.5px] text-white/60 truncate mt-0.5" />
                </div>
              </button>

              {showFavoriteToggle && (
                <button onClick={() => toggle(t)} title={fav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  className={`w-9 h-9 rounded-full grid place-items-center transition shrink-0 hover:bg-white/[0.06] ${fav ? 'text-a3' : 'text-white/45 hover:text-a4'}`}>
                  <Heart size={19} className={fav ? 'fill-a3' : ''} />
                </button>
              )}
              <AddToMenu track={t} size="md" />

              <DownloadButton track={t} size="md" />
              {onRemove && (
                <button onClick={() => onRemove(t.videoId)} title="Supprimer"
                  className="w-9 h-9 rounded-full grid place-items-center text-white/45 hover:text-rose-400 hover:bg-white/[0.06] hidden sm:grid shrink-0 transition">
                  <Trash2 size={16} />
                </button>
              )}
              <button onClick={() => play(t, tracks)} title="Lire" aria-label="Lire"
                className="shrink-0 w-9 h-9 rounded-full grid place-items-center text-white bg-gradient-to-br from-a2 to-a1 shadow-[0_6px_18px_rgb(var(--a2)/.45)] hover:brightness-110 active:scale-95 transition">
                <Play size={15} className="ml-[2px] fill-white" />
              </button>
            </div>
            {/* Indicateur d'insertion en fin de liste */}
            {dragging && i === tracks.length - 1 && overIndex === tracks.length && dragIndex !== tracks.length - 1 && insertLine}
          </div>
        )
      })}
    </div>
  )
}

function decodeEntities(s: string) {
  return s.replaceAll('&amp;', '&').replaceAll('&quot;', '"').replaceAll('&#39;', "'").replaceAll('&lt;', '<').replaceAll('&gt;', '>')
}
